import React from 'react';
import { BlockMath } from 'react-katex';
import { Link } from 'react-router-dom';
import { cn } from '@Utils';

import { RichTextSegment } from '@Models';
import { getCalloutStyles, getTextColorClass } from './helpers';

interface RichTextProps {
  segments: RichTextSegment[];
  className?: string;
  defaultColor?: string;
}

const RichText = ({ segments, className = '', defaultColor = 'text-white' }: RichTextProps) => {
  const isWhiteTheme = defaultColor === 'text-white';
  const linkTextColor = isWhiteTheme ? 'text-gray-400' : 'text-gray-700';
  const linkHoverColor = isWhiteTheme ? 'hover:text-white' : 'hover:text-gray-400';

  return (
    <span className={className}>
      {segments.map((segment, idx) => {
        // Handle inline equations
        if (segment.type === 'equation') {
          return (
            <div
              key={(segment.text ?? '') + String(idx)}
              className="px-6 py-4 rounded-lg overflow-x-auto [&_.katex-html]:!hidden [&_.katex]:text-xl">
              <BlockMath math={segment.expression || ''} />
            </div>
          );
        }

        // Calculate text styles (shared for all text types)
        const textContent = segment.text;
        const textStyles = cn(
          segment.styles?.bold && 'font-bold',
          segment.styles?.italic && 'italic',
          segment.styles?.underline && 'underline',
          segment.styles?.strikethrough && 'line-through',
          segment.styles?.code && 'bg-gray-300 p-1 rounded-lg text-sm',
          segment.styles?.code ? "font-['Fira_Code']" : '',
          segment.styles?.code ? 'text-red-700' : getTextColorClass(segment.styles?.color || 'default', defaultColor),
          getCalloutStyles(segment.styles?.color || ''),
        );

        // Handle link mentions
        if (segment.type === 'link_mention') {
          return (
            <a
              key={(segment.text ?? '') + String(idx)}
              href={segment.href}
              target="_blank"
              rel="noopener noreferrer"
              className="underline decoration-gray-700 hover:decoration-white inline-flex items-center gap-2 px-2 py-1 hover:bg-gray-400 rounded transition-colors duration-150 group">
              {segment.icon_url && <img src={segment.icon_url} alt="" className="w-4 h-4" />}
              <span className="text-gray-700 group-hover:text-white">{segment.title || segment.text}</span>
            </a>
          );
        }

        // Handle user mentions - just render as text with styles
        if (segment.type === 'user_mention') {
          return <span key={(segment.text ?? '') + String(idx)}>{segment.text}</span>;
        }

        // If segment has a link
        if (segment.link) {
          // Internal Notion page reference (starts with /)
          if (segment.link.startsWith('/')) {
            const pageId = segment.link.substring(1); // Remove leading /
            return (
              <Link
                key={(segment.text ?? '') + String(idx)}
                to={`/documentation/${pageId}`}
                className="underline decoration-gray-700 hover:decoration-white inline-flex items-center gap-1 px-1 hover:bg-gray-400 rounded transition-colors duration-150 group">
                <span className={cn(linkTextColor, 'group-hover:text-white font-bold')}>{textContent}</span>
              </Link>
            );
          }

          // External URL (http://, https://, or mailto:)
          if (
            segment.link.startsWith('http://') ||
            segment.link.startsWith('https://') ||
            segment.link.startsWith('mailto:')
          ) {
            const isMailto = segment.link.startsWith('mailto:');
            return (
              <a
                key={(segment.text ?? '') + String(idx)}
                href={segment.link}
                {...(!isMailto && { target: '_blank', rel: 'noopener noreferrer' })}
                className={cn(
                  textStyles,
                  'underline decoration-gray-600 hover:decoration-gray-400 transition-colors duration-150',
                  linkTextColor,
                  linkHoverColor,
                )}>
                {textContent}
              </a>
            );
          }
        }

        // Regular text without link
        return (
          <span key={(segment.text ?? '') + String(idx)} className={textStyles}>
            {textContent}
          </span>
        );
      })}
    </span>
  );
};

export default RichText;
